import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Coffee, Home as HomeIcon, Heart, ArrowRight, Instagram, Play } from "lucide-react";
import { useContent } from "@/lib/content-context";
import { fileUrl } from "@/lib/api";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";

const ICONS = { BookOpen, Coffee, Home: HomeIcon };

export default function Home() {
    const { content } = useContent();
    if (!content) return <div className="h-screen bg-cream-light" />;

    return (
        <div data-testid="home-page">
            <SEO title="" description={content?.story?.paragraphs?.[0]} image={fileUrl(content?.hero?.poster_url)} path="/" />
            <Hero hero={content.hero} />
            <Story story={content.story} />
            <Coffees coffee={content.coffee} />
            <Foods food={content.food} />
            <Community community={content.community} />
            <InstagramGrid instagram={content.instagram} />
        </div>
    );
}

function Hero({ hero }) {
    return (
        <section className="relative w-full h-screen overflow-hidden bg-coffee" data-testid="home-hero">
            <video
                key={hero.video_url}
                autoPlay
                muted
                loop
                playsInline
                poster={fileUrl(hero.poster_url)}
                className="absolute inset-0 w-full h-full object-cover"
            >
                <source src={fileUrl(hero.video_url)} type="video/mp4" />
            </video>
            {/* warm gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-coffee via-coffee/30 to-transparent" />
            {/* top shadow for nav visibility */}
            <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-b from-coffee/60 via-coffee/25 to-transparent pointer-events-none z-[2]" />
            {/* grain */}
            <div className="grain-overlay" />

            <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pb-20 md:pb-28 max-w-[1480px] mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-3xl"
                >
                    <div className="inline-block tracked text-gold text-[10px] mb-6 px-4 py-2 border border-gold/40 bg-coffee/30 backdrop-blur-sm">
                        A Truely Indian Hideout · Est. 2018
                    </div>
                    <h1
                        className="font-editorial font-bold text-cream leading-[0.95]"
                        style={{ fontSize: "clamp(2.6rem, 6.5vw, 5.5rem)" }}
                        data-testid="hero-strapline"
                    >
                        {hero.strapline || "Great Coffee, Freshly Crafted Food— Everytime"}
                    </h1>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-6 right-6 md:right-12 z-10 tracked text-cream/60 text-[10px] flex items-center gap-2"
            >
                <span className="block w-8 h-px bg-cream/40" />
                Scroll
            </motion.div>
        </section>
    );
}

function Story({ story }) {
    return (
        <section className="relative pt-10 pb-20 md:pt-14 md:pb-28 px-6 md:px-12 bg-cream-light" data-testid="home-story">
            <div className="max-w-[1480px] mx-auto">
                {/* Top: pull-quote */}
                <Reveal>
                    <div className="text-center">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">The Hideout Story</div>
                        <h2
                            className="font-editorial font-medium text-gold leading-[1.15] lg:whitespace-nowrap"
                            style={{ fontSize: "clamp(1.4rem, 3.2vw, 2.8rem)", letterSpacing: "-0.005em" }}
                            data-testid="story-pullquote"
                        >
                            {story.pull_quote}
                        </h2>
                    </div>
                </Reveal>

                {/* Below: image left + paragraphs right, equal heights */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-10 md:mt-16 items-stretch">
                    <Reveal className="lg:col-span-6">
                        <div className="relative h-full min-h-[420px] overflow-hidden bg-coffee/10">
                            <img
                                src={fileUrl(story.image || "https://images.unsplash.com/photo-1554118811-1e0d58224f24")}
                                alt="Cafe Hideout interior"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.04]"
                            />
                            <div className="absolute bottom-5 left-5 px-3 py-1.5 bg-cream-light/90 backdrop-blur-sm">
                                <span className="tracked text-[9px] text-coffee">CAFE · BENGALURU</span>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-6 flex flex-col justify-between">
                        <div className="space-y-5 text-coffee/85 font-body text-base md:text-[17px] leading-[1.85]">
                            {story.paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                        <Link
                            to={story.cta_link || "/about"}
                            className="inline-block tracked text-gold text-[10px] mt-10 px-4 py-2 border border-gold/40 bg-coffee/5 hover:bg-coffee hover:text-cream hover:border-coffee transition-colors duration-400 self-start"
                            data-testid="story-cta"
                        >
                            {story.cta_label || "Read Our Full Story"} →
                        </Link>
                    </Reveal>
                </div>
            </div>

            <Reveal className="max-w-[1480px] mx-auto mt-20 md:mt-28">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    {story.food_cards?.map((c, i) => (
                        <div key={i} className="group cursor-pointer" data-testid={`story-card-${i}`}>
                            <div className="overflow-hidden bg-coffee/5 aspect-[4/5] mb-3">
                                <img
                                    src={fileUrl(c.image)}
                                    alt={c.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                                />
                            </div>
                            <div className="font-mono tracked text-[9px] text-coffee/50">{c.label}</div>
                            <div className="font-editorial font-bold text-xl text-coffee mt-1">{c.title}</div>
                        </div>
                    ))}
                </div>
            </Reveal>
        </section>
    );
}

function Coffees({ coffee }) {
    return (
        <section className="relative pt-12 pb-24 md:pt-16 md:pb-36 px-6 md:px-12 bg-coffee text-cream overflow-hidden" data-testid="home-coffee">
            <div className="grain-light" />
            <div className="relative max-w-[1480px] mx-auto text-center">
                <Reveal>
                    <div className="tracked text-gold text-[10px] mb-5">Our Coffee</div>
                    <h2
                        className="font-editorial font-medium text-cream leading-[1.15]"
                        style={{ fontSize: "clamp(1.7rem, 3.8vw, 3.4rem)", letterSpacing: "-0.005em" }}
                    >
                        {coffee.title}
                    </h2>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-20">
                    {coffee.items?.map((c, i) => (
                        <Reveal key={i} delay={i * 0.12}>
                            <div className="group cursor-pointer" data-testid={`coffee-card-${i}`}>
                                <div className="overflow-hidden aspect-square mb-6 bg-coffee-700/30">
                                    <img
                                        src={fileUrl(c.image)}
                                        alt={c.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                    />
                                </div>
                                <h3 className="font-editorial font-medium text-2xl md:text-3xl text-cream gold-underline inline-block">
                                    {c.name}
                                </h3>
                                <p className="font-body font-light text-cream/60 mt-3 text-sm md:text-base leading-relaxed">
                                    {c.descriptor}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.3} className="mt-16">
                    <Link to={coffee.cta_link || "/blog"} className="btn-pill btn-pill-cream" data-testid="coffee-cta">
                        {coffee.cta_label || "Explore Our Menu"} <ArrowRight size={14} />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function Foods({ food }) {
    const description = food.description || "At Hideout, the menu is small on purpose — inspired by India, crafted for today. A tight, hand-picked selection of food we love eating ourselves: toasted sandwiches, big honest burgers, rice bowls that feel like a deep breath, bright salads, pan-Asian small plates worth sharing, and a few sweet things for the road. Every plate is built fresh that morning, with produce we sourced honestly, from people we know by first name.";
    const defaultCategories = [
        { image: "https://images.unsplash.com/photo-1528735602780-2552fd46c7af?auto=format&fit=crop&w=900&q=80", title: "Sandwiches", note: "Toasted, layered, loaded." },
        { image: "https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&w=900&q=80", title: "Burgers", note: "Big, honest, hand-built. With fries." },
        { image: "https://images.pexels.com/photos/2092507/pexels-photo-2092507.jpeg", title: "Rice Bowls", note: "Mexican grain bowls. Big on flavour." },
        { image: "https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg", title: "Salads", note: "Chicken tikka salad. Indian heart, light plate." },
        { image: "https://images.unsplash.com/photo-1496116218417-1a781b1c416c?auto=format&fit=crop&w=900&q=80", title: "Pan-Asian", note: "Bao, dim sums, noodles." },
        { image: "https://images.pexels.com/photos/132694/pexels-photo-132694.jpeg", title: "Cafe Sweets", note: "Chocolate truffle cake. Pairs with everything." },
    ];
    const categories = food.categories && food.categories.length > 0 ? food.categories : defaultCategories;

    return (
        <section className="relative pt-14 pb-24 md:pt-20 md:pb-32 px-6 md:px-12 bg-cream-light overflow-hidden" data-testid="home-food">
            <div
                aria-hidden
                className="absolute top-12 right-0 left-0 text-center font-headings font-bold text-coffee pointer-events-none select-none"
                style={{
                    fontSize: "clamp(8rem, 24vw, 22rem)",
                    opacity: 0.06,
                    letterSpacing: "0.04em",
                    lineHeight: 0.85,
                }}
            >
                {food.watermark || "FOOD"}
            </div>

            <div className="relative max-w-[1480px] mx-auto">
                <Reveal>
                    <div className="text-center">
                        <div className="tracked text-coffee/50 text-[10px] mb-5">Our Food</div>
                        <h2
                            className="font-editorial font-bold text-coffee leading-[0.95] mx-auto"
                            style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
                        >
                            {food.title}
                        </h2>
                    </div>
                </Reveal>

                {/* Description paragraph */}
                <Reveal delay={0.1} className="mt-10 md:mt-14">
                    <p className="font-body font-light text-coffee/80 text-base md:text-[17px] leading-[1.9] max-w-3xl mx-auto text-center">
                        {description}
                    </p>
                </Reveal>

                {/* Four pillars */}
                <Reveal delay={0.15} className="mt-12 md:mt-16">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-y border-coffee/15 py-10">
                        {[
                            { kicker: "01", label: "Variety", note: "A short, sharp menu of café favourites." },
                            { kicker: "02", label: "Freshly Crafted", note: "Built that morning, never the night before." },
                            { kicker: "03", label: "Handpicked", note: "Recipes we eat ourselves." },
                            { kicker: "04", label: "Honest Sourcing", note: "Produce from people we know." },
                        ].map((p, i) => (
                            <div key={i} className="text-center md:text-left">
                                <div className="font-mono tracked text-gold text-[10px] mb-2">{p.kicker}</div>
                                <div className="font-editorial font-medium text-coffee text-xl md:text-2xl mb-2">{p.label}</div>
                                <div className="font-body font-light text-coffee/65 text-sm leading-relaxed">{p.note}</div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Six food category cards */}
                <Reveal delay={0.2} className="mt-12 md:mt-16">
                    <div className="text-center mb-10">
                        <div className="tracked text-coffee/50 text-[10px]">Inspired by India · Crafted for Today</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-[1180px] mx-auto">
                        {categories.map((card, i) => (
                            <div key={i} className="group cursor-pointer" data-testid={`food-cat-${i}`}>
                                <div className="aspect-[5/3] overflow-hidden bg-coffee/5 mb-5">
                                    <img
                                        src={fileUrl(card.image)}
                                        alt={card.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-[1.06]"
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <h3 className="font-editorial font-medium text-coffee text-xl md:text-2xl gold-underline inline-block">
                                        {card.title}
                                    </h3>
                                    <span className="font-mono tracked text-coffee/40 text-[10px]">0{i + 1}</span>
                                </div>
                                <p className="font-body font-light text-coffee/60 text-sm mt-2 leading-relaxed">
                                    {card.note}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function Community({ community }) {
    const communes = community?.communes && community.communes.length > 0 ? community.communes : [
        { name: "Cricket Team", tag: "Saturday Mornings", note: "Bats, balls, beans. The Hideout XI plays in the park every Saturday — and yes, sandwiches come along.", image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=900&q=80", doodle: "cricket" },
        { name: "Music Nights", tag: "Every Thursday", note: "Acoustic sets, local bands, open mic. The counter turns into a stage. The chairs turn into front rows.", image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=900&q=80", doodle: "music" },
        { name: "Laugh Club", tag: "First Fridays", note: "Stand-up sets from comics you'll hear on Netflix in two years. Two-drink minimum is optional.", image: "https://images.unsplash.com/photo-1585699324551-f6c309eedeca?auto=format&fit=crop&w=900&q=80", doodle: "laugh" },
        { name: "Book Club", tag: "Last Sunday", note: "A shelf at the back. A book a month. Coffee on the house if you finish it before the meet.", image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=900&q=80", doodle: "book" },
        { name: "Art Sundays", tag: "Twice a Month", note: "Sketchbooks, watercolours, pottery, photography. Beginners and pros. No critique. Just colour.", image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=900&q=80", doodle: "art" },
        { name: "We're Social", tag: "Year Round", note: "Morning runs, Mother's Day brunches, Republic Day breakfasts, monsoon meet-ups. Whenever the city needs a reason — we host one.", image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=900&q=80", doodle: "social" },
    ];

    return (
        <section className="relative bg-cream pt-20 md:pt-28 pb-24 md:pb-32 px-6 md:px-12 overflow-hidden" data-testid="home-community">
            {/* Decorative doodles */}
            <Doodle name="scribble" className="absolute top-10 left-6 md:left-16 w-24 md:w-32 text-gold/50 -rotate-12" />
            <Doodle name="star" className="absolute top-32 right-6 md:right-20 w-12 md:w-16 text-coffee/40 rotate-12" />
            <Doodle name="arrow" className="absolute bottom-40 left-10 md:left-32 w-16 md:w-24 text-gold/60 rotate-[-20deg] hidden md:block" />

            <div className="relative max-w-[1480px] mx-auto">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
                    <Reveal className="lg:col-span-7">
                        <div className="tracked text-coffee/50 text-[10px] mb-5">The Hideout Commune</div>
                        <h2
                            className="font-editorial font-bold text-coffee leading-[0.98]"
                            style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)" }}
                        >
                            We don't run a cafe.<br />
                            We run a <span className="relative inline-block">
                                <span className="relative z-10">commune.</span>
                                <span aria-hidden className="absolute left-0 right-0 bottom-1 md:bottom-2 h-2 md:h-3 bg-gold/40 -rotate-1 z-0" />
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <p className="font-body font-light text-coffee/80 text-base md:text-[17px] leading-[1.9]">
                            Coffee is the excuse, not the point. Inside every Hideout there's a cricket team, a band, a book club, a comedy night, an art huddle. Strangers walk in. Friends walk out. The counter is just where it starts.
                        </p>
                    </Reveal>
                </div>

                {/* The 6 communes — two rows of 3 (3 + 3) */}
                <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
                    {communes.slice(0, 6).map((c, i) => {
                        return (
                            <Reveal key={i} delay={i * 0.07} className="md:col-span-4 group cursor-pointer">
                                <div className="relative aspect-[5/4] overflow-hidden bg-coffee/5 mb-4">
                                    <img
                                        src={fileUrl(c.image)}
                                        alt={c.name}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-coffee/85 via-coffee/20 to-transparent" />
                                    <Doodle name={c.doodle} className="absolute top-5 right-5 w-14 md:w-16 text-gold opacity-90" />
                                    <div className="absolute bottom-5 left-5 right-5 text-cream">
                                        <div className="font-mono tracked text-gold text-[10px] mb-1">{c.tag}</div>
                                        <h3 className="font-editorial font-bold text-2xl md:text-3xl leading-[0.98]">{c.name}</h3>
                                    </div>
                                    <div className="absolute top-5 left-5 w-8 h-8 rounded-full bg-cream-light/95 flex items-center justify-center font-mono text-[10px] text-coffee">0{i + 1}</div>
                                </div>
                                <p className="font-body font-light text-coffee/75 text-sm leading-relaxed">{c.note}</p>
                            </Reveal>
                        );
                    })}
                </div>

                {/* CTA banner */}
                <Reveal delay={0.1} className="mt-20 md:mt-24">
                    <Link
                        to={community.cta_banner?.link || "/contact"}
                        className="block bg-coffee text-cream py-10 md:py-14 px-8 md:px-14 group hover:bg-coffee-800 transition-colors duration-500 relative overflow-hidden"
                        data-testid="community-cta-banner"
                    >
                        <Doodle name="scribble" className="absolute top-4 right-10 w-20 text-gold/30 hidden md:block" />
                        <div className="relative flex items-center justify-between gap-6 flex-wrap">
                            <div>
                                <div className="tracked text-gold text-[10px] mb-2">Join The Commune</div>
                                <span
                                    className="font-editorial font-bold text-cream block"
                                    style={{ fontSize: "clamp(1.8rem, 4vw, 3.4rem)" }}
                                >
                                    {community.cta_banner?.label || "Find Your Hideout"}
                                </span>
                            </div>
                            <ArrowRight
                                size={48}
                                strokeWidth={1}
                                className="text-gold transition-transform duration-500 group-hover:translate-x-3"
                            />
                        </div>
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function Doodle({ name, className = "" }) {
    const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
    const paths = {
        cricket: <g {...stroke}><path d="M10 50 L40 20 L46 26 L16 56 Z" /><circle cx="55" cy="60" r="6" /></g>,
        music: <g {...stroke}><path d="M25 50 L25 18 L55 12 L55 44" /><circle cx="22" cy="52" r="6" /><circle cx="52" cy="46" r="6" /></g>,
        laugh: <g {...stroke}><circle cx="32" cy="32" r="22" /><path d="M22 35 Q32 48 42 35" /><path d="M22 22 L26 26" /><path d="M42 22 L38 26" /></g>,
        book: <g {...stroke}><path d="M10 14 L10 50 L32 46 L32 18 Z" /><path d="M32 18 L54 14 L54 50 L32 46" /><path d="M32 18 L32 46" /></g>,
        art: <g {...stroke}><path d="M14 50 L26 32 L36 42 L48 22 L54 50 Z" /><circle cx="22" cy="20" r="4" /></g>,
        social: <g {...stroke}><circle cx="22" cy="26" r="8" /><circle cx="44" cy="22" r="6" /><path d="M8 52 C 12 40, 32 40, 36 52" /><path d="M34 50 C 38 42, 54 42, 56 52" /></g>,
        scribble: <g {...stroke}><path d="M5 30 C 15 10, 25 50, 35 30 S 55 10, 75 30 S 95 50, 115 30" /></g>,
        star: <g {...stroke}><path d="M32 8 L38 24 L56 24 L42 36 L48 54 L32 44 L16 54 L22 36 L8 24 L26 24 Z" /></g>,
        arrow: <g {...stroke}><path d="M8 30 C 30 10, 60 10, 80 30" /><path d="M70 22 L80 30 L74 40" /></g>,
    };
    const viewBoxes = { scribble: "0 0 120 60", arrow: "0 0 90 50", default: "0 0 64 64" };
    return (
        <svg className={className} viewBox={viewBoxes[name] || viewBoxes.default} xmlns="http://www.w3.org/2000/svg" aria-hidden>
            {paths[name] || paths.scribble}
        </svg>
    );
}

function InstagramGrid({ instagram }) {
    const [posts, setPosts] = useState(null); // null = loading, [] = no posts, [...] = live
    const [error, setError] = useState(false);

    useEffect(() => {
        let mounted = true;
        api.get("/instagram?limit=12")
            .then((r) => {
                if (!mounted) return;
                const items = r.data?.items || [];
                setPosts(items);
            })
            .catch(() => mounted && setError(true));
        return () => { mounted = false; };
    }, []);

    const useLive = posts && posts.length > 0;
    const tiles = instagram.tiles?.slice(0, 16) || [];
    const showFallback = (!useLive && tiles.length > 0) || error;

    return (
        <section className="relative bg-cream-light py-24 md:py-32 px-6 md:px-12" data-testid="home-instagram">
            <div className="max-w-[1480px] mx-auto">
                <Reveal className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="tracked text-coffee/50 text-[10px] mb-3">From The Feed</div>
                        <div className="flex items-center gap-3">
                            <Instagram size={26} strokeWidth={1.2} className="text-coffee" />
                            <span className="font-editorial font-bold text-3xl md:text-4xl text-coffee">
                                {instagram.handle}
                            </span>
                            {useLive && (
                                <span className="inline-flex items-center gap-1.5 ml-2 px-2 py-0.5 bg-gold/15 border border-gold/40 rounded-full">
                                    <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
                                    <span className="font-mono tracked text-gold text-[9px]">LIVE</span>
                                </span>
                            )}
                        </div>
                    </div>
                    <a
                        href={instagram.cta_link || `https://instagram.com/${(instagram.handle || "").replace("@", "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-pill btn-pill-coffee"
                        data-testid="instagram-cta"
                    >
                        {instagram.cta_label || "Follow The Story"} <ArrowRight size={14} />
                    </a>
                </Reveal>

                <Reveal delay={0.1}>
                    {useLive ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5" data-testid="ig-live-grid">
                            {posts.slice(0, 12).map((p, i) => {
                                const isVideo = p.media_type === "VIDEO";
                                const isCarousel = p.media_type === "CAROUSEL_ALBUM";
                                let imgSrc = p.thumbnail_url || p.media_url;
                                // For carousels Instagram returns media_url on the parent post; prefer the first child image
                                if (isCarousel && Array.isArray(p.children) && p.children.length > 0) {
                                    const first = p.children[0];
                                    imgSrc = first.thumbnail_url || first.media_url || imgSrc;
                                }
                                return (
                                    <a
                                        key={p.media_id}
                                        href={p.permalink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="relative aspect-square overflow-hidden group bg-coffee/5"
                                        data-testid={`ig-live-${i}`}
                                    >
                                        <img
                                            src={imgSrc}
                                            alt={(p.caption || "cafe hideout").slice(0, 80)}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                                        />
                                        {isVideo && (
                                            <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-coffee/70 backdrop-blur-sm flex items-center justify-center">
                                                <Play size={12} className="text-cream fill-cream" />
                                            </div>
                                        )}
                                        {isCarousel && (
                                            <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-coffee/70 backdrop-blur-sm rounded">
                                                <span className="font-mono text-cream text-[9px]">{(p.children || []).length || "•••"}</span>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-coffee/85 via-coffee/0 to-transparent opacity-0 group-hover:opacity-100 flex items-end p-3 transition-opacity duration-500">
                                            <p className="font-body text-cream text-[11px] leading-snug line-clamp-3">
                                                {p.caption || ""}
                                            </p>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    ) : posts === null && !error ? (
                        // Loading skeleton
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="aspect-square bg-coffee/5 animate-pulse" />
                            ))}
                        </div>
                    ) : showFallback ? (
                        // Fallback: manual CMS tiles
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
                            {tiles.map((url, i) => (
                                <a
                                    key={i}
                                    href={instagram.cta_link || "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="relative aspect-square overflow-hidden group bg-coffee/5"
                                    data-testid={`instagram-tile-${i}`}
                                >
                                    <img
                                        src={fileUrl(url)}
                                        alt="cafe hideout"
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-coffee/70 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-500">
                                        <Heart size={28} strokeWidth={1.4} className="text-gold" />
                                    </div>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-coffee/50 font-body" data-testid="ig-empty">
                            Feed loading. Check back in a moment.
                        </div>
                    )}
                </Reveal>
            </div>
        </section>
    );
}
